const getHeaders = () => {
  const token = document.querySelector('meta[name="csrf-token"]');
  if (!token) {
    throw new Error('CSRF token not found');
  }
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': token.content
  };
};

const setLoading = (element, isLoading) => {
  element.classList.toggle('processing', isLoading);
  element.disabled = isLoading;
};

const showFlashMessage = (type, message) => {
  const container = document.getElementById('flash-message-container') ||
                    document.getElementById('flash-messages');
  if (!container) return;

  const flashMessage = document.createElement('div');
  flashMessage.className = `alert alert-${type} alert-dismissible fade show`;
  flashMessage.innerHTML = `
    ${message}
    <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
  `;
  container.insertBefore(flashMessage, container.firstChild);
  setTimeout(() => flashMessage.remove(), 5000);
};

const updateStatusLabel = (container, isApproved) => {
  const statusLabel = container.querySelector('.status-label');
  if (statusLabel) {
    statusLabel.textContent = isApproved ? 'Approved' : 'Pending';
    statusLabel.className = `status-label me-2 ${isApproved ? 'text-success' : 'text-danger'}`;
  }
};

const updateRoleLabel = (container, isActive) => {
  const roleLabel = container.querySelector('.role-name');
  if (roleLabel) {
    roleLabel.classList.toggle('text-success', isActive);
  }
};

const defaultManagementUrls = () => {
  if (window.location.pathname.startsWith('/superuser')) {
    return {
      approval: '/superuser/dashboard/update_user_approval',
      squadron: '/superuser/dashboard/update_user_squadron',
      role: '/superuser/dashboard/update_user_role'
    };
  }

  return {
    approval: '/users/update_user_approval',
    squadron: '/users/update_user_squadron',
    role: '/users/update_user_role'
  };
};

const onUserManagementPage = () => {
  return document.querySelector('[data-user-management]') ||
         window.location.pathname === '/users' ||
         window.location.pathname.startsWith('/users?') ||
         window.location.pathname.startsWith('/superuser/dashboard');
};

const getManagementUrls = () => {
  if (!onUserManagementPage()) return null;

  const defaults = defaultManagementUrls();
  const root = document.querySelector('[data-user-management]');
  if (!root) return defaults;

  return {
    approval: root.dataset.approvalUrl || defaults.approval,
    squadron: root.dataset.squadronUrl || defaults.squadron,
    role: root.dataset.roleUrl || defaults.role
  };
};

let searchTimeout;

const initSearch = () => {
  const searchForm = document.getElementById('user-search-form');
  const searchInput = document.getElementById('user-search-input');
  if (!searchForm || !searchInput || searchForm.dataset.searchBound === 'true') return;

  searchForm.dataset.searchBound = 'true';

  const filterUserCards = () => {
    const searchTerm = searchInput.value.toLowerCase().trim();
    document.querySelectorAll('.user-card').forEach((card) => {
      const userName = card.querySelector('.user-name')?.textContent.toLowerCase() || '';
      const column = card.closest('.col-md-4');
      if (column) {
        column.style.display = userName.includes(searchTerm) ? '' : 'none';
      }
    });
  };

  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(filterUserCards, 300);
  });

  searchForm.addEventListener('submit', (e) => {
    e.preventDefault();
    filterUserCards();
  });
};

document.addEventListener('turbo:load', initSearch);
document.addEventListener('DOMContentLoaded', initSearch);

document.addEventListener('change', async (event) => {
  const urls = getManagementUrls();
  if (!urls) return;

  if (event.target.matches('.approval-toggle-input')) {
    await handleApprovalChange(event.target, urls.approval);
  } else if (event.target.matches('.squadron-select-input')) {
    await handleSquadronChange(
      event.target,
      event.target.dataset.squadronUrl || urls.squadron
    );
  } else if (event.target.matches('.toggle-input')) {
    await handleRoleChange(event.target, urls.role);
  }
});

async function handleApprovalChange(toggle, url) {
  const userId = toggle.dataset.userId;
  const isApproved = toggle.checked;
  const originalState = !isApproved;
  const container = toggle.closest('.account-approval');

  setLoading(toggle, true);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        user: {
          id: userId,
          approved: isApproved
        }
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      updateStatusLabel(container, isApproved);
      showFlashMessage('success', 'Account status updated successfully');
    } else {
      toggle.checked = originalState;
      updateStatusLabel(container, originalState);
      showFlashMessage('danger', data.error || 'Failed to update status');
    }
  } catch (error) {
    console.error('Error:', error);
    toggle.checked = originalState;
    updateStatusLabel(container, originalState);
    showFlashMessage('danger', 'Network error occurred while updating approval status');
  } finally {
    setLoading(toggle, false);
  }
}

async function handleSquadronChange(select, url) {
  const userId = select.dataset.userId;
  const squadronId = select.value;
  const previousValue = select.dataset.currentSquadron || '';

  setLoading(select, true);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        id: userId,
        squadron_id: squadronId
      })
    });

    const data = await response.json();
    if (data.success) {
      showFlashMessage('success', 'Squadron updated successfully');
      const squadronDisplay = select.closest('.squadron-select')?.querySelector('.squadron-display');
      if (squadronDisplay) {
        squadronDisplay.textContent = data.squadron || 'None';
      }
      select.dataset.currentSquadron = squadronId;
    } else {
      select.value = previousValue;
      showFlashMessage('danger', data.error || 'Failed to update squadron');
    }
  } catch (error) {
    console.error('Error:', error);
    select.value = previousValue;
    showFlashMessage('danger', 'Network error occurred while updating squadron');
  } finally {
    setLoading(select, false);
  }
}

async function handleRoleChange(toggle, url) {
  const roleToggle = toggle.closest('.role-toggle');
  const userId = toggle.dataset.userId;
  const roleId = toggle.dataset.roleId;
  const isChecked = toggle.checked;
  const originalState = !isChecked;

  setLoading(toggle, true);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        dashboard: {
          id: userId,
          role_id: roleId,
          role_action: isChecked ? 'add' : 'remove'
        }
      })
    });

    const data = await response.json();

    if (data.success) {
      showFlashMessage('success', data.message);
      toggle.checked = isChecked;
      updateRoleLabel(roleToggle, isChecked);
    } else {
      toggle.checked = originalState;
      updateRoleLabel(roleToggle, originalState);
      showFlashMessage('danger', data.error || 'Failed to update role');
    }
  } catch (error) {
    console.error('Error:', error);
    toggle.checked = originalState;
    updateRoleLabel(roleToggle, originalState);
    showFlashMessage('danger', 'Network error occurred while updating role');
  } finally {
    setLoading(toggle, false);
  }
}
